import { cva } from 'class-variance-authority';
import { PHONE_INPUT_SIZE } from 'components/styles/phoneInputSize';

/**
 * Multi-line text input class recipes.
 *
 * Like `Button.recipe` and `TextInput.recipe`, these contain ZERO `dark:` variants — colour comes
 * entirely from the `--ds-textarea-*` slots that `.ds-textarea` sets in
 * `components/styles/design-system.css`, and that class needs no dark rule at all because every one
 * of its slots names a semantic role.
 *
 * Every state selector reads Base UI's `Field.Root` attributes through the `group/ti` marker
 * (`data-focused`, `data-filled`, `data-invalid`, `data-disabled`), so there is no React state
 * behind the shrink or the colour changes — which also means the states are reachable from a story
 * or a test by rendering, not by simulating.
 *
 * The state selectors are MUTUALLY EXCLUSIVE rather than relying on utility sort order, the same
 * discipline the button and text input recipes use. Precedence, highest first:
 *
 *   disabled → invalid → focused → hover → resting
 *
 * An errored field therefore stays red while focused, which is what the design's separate Error
 * and Active cells imply.
 *
 * ── The set was REBUILT, and this file follows it rather than the one it was ported from ───────
 *
 * Read off node 1437:128678 (page "Text-area [In review]") — 48 cells across State x Position x
 * isFilled x isDisabled x hasError. The previous source, node 1020-25860, is gone: thirty cells at
 * a fixed 300x100, a `background/primary` fill, a `border/focus` focused edge, two hand-mixed
 * translucent tints and no trailing affordance. The library rebuilt the component on `Text Input`'s
 * anatomy (447:35126), so every reading below now matches `.ds-input`'s and the six state columns
 * are the same six.
 *
 *   Default    background/control/default   border/control/default    1px
 *   Hover      background/control/hover     border/control/hover      1px
 *   Active     background/control/default   border/accent/default     2px   + the caret
 *   Focus      background/control/default   border/control/default    1px   + the `Focused` effect
 *   Error      background/control/default   border/destructive        1px
 *   Disabled   background/control/disabled  border/control/disabled   1px
 *
 * `Focus` and `Active` are two independent LAYERS rather than two readings of one state, exactly as
 * `textInputBoxRecipe` sets out at length: `Active` is "this field holds the caret" and is produced
 * by any focus, pointer or keyboard; `Focus` is the accessibility ring and is additive on top of
 * it. Read that way the `Focus` cell stops looking impossible — it draws a resting border and no
 * caret, which a real field can never show, but as a layer shown on its own that is just "here is
 * the ring". The keyboard-only half is not expressible in CSS, so it is not drawn — see the halo
 * note on `textAreaBoxRecipe`.
 *
 * ── Why none of this imports `TextInput.recipe` ────────────────────────────────────────────────
 *
 * The geometry below is a near-copy of `textInputBoxRecipe`'s and importing it would be less code.
 * It is restated anyway, and the rework makes the temptation stronger rather than weaker, because
 * the VALUES now agree where they used to diverge.
 *
 * The first reason is the namespace. `textInputRootRecipe()` emits the literal string `ds-input`,
 * and `.ds-input` carries a dark rule this class deliberately does not have — so pulling it in
 * would drag a theme-conditional block into a component whose whole claim is that it needs none.
 *
 * The second is that the duplication is GATED rather than trusted. `Select` reuses the recipe and
 * calls the result drift-proof "by construction rather than by matching values that could later
 * diverge"; dropping the reuse drops that guarantee, so it comes back as an assertion —
 * `TextArea.test.tsx` imports both recipes and compares the radius, the border-width slot reference
 * and the height floors for every `position`. That test is load-bearing, not decorative: it is the
 * only thing standing between these two files and a silent one-pixel disagreement, because the slot
 * harness cannot see a Tailwind literal in a TypeScript file.
 *
 * ── Where the padding lives ────────────────────────────────────────────────────────────────────
 *
 * All of it is on the CONTROL, none on the box, exactly as on `TextInput` and for exactly the same
 * reason: padding the box would leave the `<textarea>` a strip inside a larger box, so clicking
 * near the edges would do nothing and the caret would land in the wrong place. Paying the padding
 * from inside the control makes the element fill the box, so every pixel of the field is a hit
 * target.
 *
 * It matters MORE here than it does next door. A single-line field's dead zone is at worst the 20px
 * above and below one line of text; a five-row field's would be the whole left and right gutter
 * down its entire height, which is where a user reaches to place a caret mid-paragraph.
 *
 * The one exception is the bottom inset when helper text is present, which the control can no
 * longer pay for because the helper sits below it.
 */

/**
 * Marker + layout for `Field.Root`.
 *
 * The `.ds-textarea` slot class lives HERE rather than on the bordered box, for the reason
 * `textInputRootRecipe` documents: custom properties cascade, so everything inside inherits them,
 * and anything that later renders as a SIBLING of the box would otherwise fall outside their scope
 * and resolve to inherited colour instead of failing visibly.
 *
 * The group marker is `group/ti` rather than a name of its own. It is shared with `TextInput` on
 * purpose — the two are alternative fields, never nested one inside the other, so there is no
 * ambiguity to resolve — and sharing it means a stacked group's state selectors read the same
 * whichever field type is in it.
 */
export const textAreaRootRecipe = cva(['group/ti ds-textarea', 'relative flex w-full flex-col']);

/**
 * The bordered box.
 *
 * Holds no padding of its own (see the note above) — it is a border, a fill, a radius and the
 * bottom inset that appears with helper text.
 *
 * `gap-[4px]` separates the value block from the helper text, which is the set's own `x1` root
 * spacing. The label is absolutely positioned and out of flow, so the gap only ever applies between
 * the control and the helper row — a field without helper text gets no stray space.
 *
 * ── Why there is no `min-h-*` here, unlike every sibling field ────────────────────────────────
 *
 * The floor lives on the CONTROL (`textAreaControlRecipe`), and that is a deliberate move rather
 * than an omission. It used to be `min-h-[100px]` on this element, read off the retired set's fixed
 * 300x100 cells.
 *
 * The rebuilt set floors the CONTENT, not the box: `Container` carries `minHeight: x16` = 64px, and
 * the box hugs that plus its own 8px insets and 1px border, which is how every cell comes out at
 * exactly 82px with no helper and 106px with one. Putting a single number on the box cannot express
 * that, because the helper row is inside the border and shifts what the total has to be — the old
 * 100px floor was right for neither. Flooring the control instead reproduces both totals exactly
 * and puts the `x16` token where a reader can see it. `TextArea.test.tsx` asserts both totals.
 *
 * ── Why the accent border is painted as a shadow rather than a wider border ────────────────────
 *
 * `border-width` NEVER changes here, in any state, and the reason is the one `textInputBoxRecipe`
 * spells out: a border is layout, so thickening it on focus moves the padding box and shifts the
 * absolutely positioned label and the value text. On a multi-line field that jump is more visible,
 * not less — every line of a filled paragraph moves at once.
 *
 * So the border stays at its resting 1px and the second pixel is painted as an inset `box-shadow`,
 * which occupies no layout space:
 *
 *   inset 0 0 0 1px  --ds-textarea-stroke-extra   the pixel that makes the focused stroke 2px
 *
 * It defaults to `transparent`, which is what lets the declaration be unconditional — only the slot
 * is switched. `Active` is the only cell that needs it now: the retired set authored error and
 * disabled at 1.5px as well, and the rebuilt one draws both at 1px, so those two rules are deleted
 * rather than retuned and the remaining one paints a whole pixel rather than a half.
 *
 * Note this means a focused field does NOT shift its contents by a pixel the way the Figma cell
 * does — the drawn Active cell grows to 84px because Figma adds stroke weight to a hugging frame,
 * and its children sit at y=10 instead of y=9. Reproducing that would be reproducing a Figma
 * artefact as a design decision.
 *
 * ── The focus halo is NOT DRAWN, and this component is where that was a live defect ────────────
 *
 * `Focus` asks for a keyboard-only ring. **CSS cannot express that on a text control**, and the
 * measurement is unambiguous rather than a matter of opinion: a `<textarea>` matches
 * `:focus-visible` on a PLAIN MOUSE CLICK. That is deliberate UA behaviour and not
 * Chrome-specific — the spec has UAs match `:focus-visible` whenever the focused element takes text
 * input, on the reasoning that the user is about to type and wants to see where.
 *
 * This file used to draw a 2px offset outline from `has-[:focus-visible]` and assert that it answered
 * a Tab and not a click. That was wrong: a mouse click sets `:focus-visible` on the textarea too.
 * It is removed here rather than re-keyed, because there is no selector to re-key it to.
 *
 * Nothing accessible is lost. The caret state's accent border measures 9.11:1 light and 4.70:1 dark
 * against this fill (re-measured 2026-09-18, after the light control ladder moved to neutral/50)
 * and clears 3:1 against every host a field sits on, at 2px — it satisfies 1.4.11
 * and 2.4.11 on its own. What is lost is the keyboard-only DISTINCTION, and
 * `--ds-textarea-halo-inner` / `-outer` stay declared as what will paint it once
 * input-modality tracking exists. That is shared infrastructure — `DatePicker` and `Select` want
 * the same answer, and giving it to one field alone would make a mixed `FieldGroup` answer a Tab
 * two different ways.
 */
export const textAreaBoxRecipe = cva(
  [
    'relative flex w-full flex-col gap-[4px]',
    'border-solid border-[length:var(--ds-textarea-border-width)] border-[var(--ds-textarea-border)]',
    'bg-[var(--ds-textarea-bg)]',
    'cursor-text',
    'transition-[border-color,background-color,box-shadow] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

    /**
     * Hover is excluded from every other state so it can never fight them. It now moves the FILL as
     * well as the border, which is new to this component — see `--ds-textarea-bg-hover`.
     *
     * There is no `:not([data-open])` fence, unlike `textInputBoxRecipe`. That attribute is
     * `Select`'s engaged state and this component has no equivalent cell: the rebuilt set has no
     * open column, nothing writes `data-open` on a `TextArea`'s root, and a fence against a state
     * that cannot occur reads as a state that can.
     */
    'group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:border-[var(--ds-textarea-border-hover)]',
    'group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:bg-[var(--ds-textarea-bg-hover)]',

    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:border-[var(--ds-textarea-border-caret)]',
    'group-[[data-invalid]:not([data-disabled])]/ti:border-[var(--ds-textarea-border-invalid)]',
    'group-data-[disabled]/ti:border-[var(--ds-textarea-border-disabled)]',
    'group-data-[disabled]/ti:bg-[var(--ds-textarea-bg-disabled)]',
    'group-data-[disabled]/ti:cursor-not-allowed',

    // Off by default, so the declaration below can be unconditional. One layer now that the tints
    // are gone and the halo is not drawn — and the slot is kept rather than inlining the colour.
    '[--ds-textarea-stroke-extra:transparent]',
    'shadow-[inset_0_0_0_1px_var(--ds-textarea-stroke-extra)]',

    // The second pixel of the focused stroke, in the same colour as the border showing beneath it.
    // Excluded from invalid, which keeps its red border at 1px as the set draws it.
    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:[--ds-textarea-stroke-extra:var(--ds-textarea-border-caret)]',
  ],
  {
    variants: {
      /**
       * Figma's `Position` axis, which the rebuilt set draws in full — all four values have cells,
       * so `middle` is no longer the extrapolation the retired set left it as. The axis was also
       * renamed: it used to be `Corner Radius`, whose values named which EDGE was squared and so
       * read inverted against these. See `TextInputPosition`, which this component imports rather
       * than re-declaring.
       */
      position: {
        standalone: 'rounded-[16px]',
        first: 'rounded-t-[16px] rounded-b-none',
        middle: 'rounded-none',
        last: 'rounded-b-[16px] rounded-t-none',
      },
      /**
       * The bottom inset, which moves between the box and the control depending on whether anything
       * is rendered below the value. A plain 8px — the set's own `x2` root padding — because the
       * border never changes width and so nothing here has to compensate for it.
       *
       * `textInputBoxRecipe` pays 7px in the same slot, and the missing pixel there is its own
       * min-height slack being handed back. This recipe has no such debt: the floor is on the
       * control, so the slack is inside the element that pays the padding and never has to be
       * smuggled across.
       */
      hasSubLabel: {
        true: 'pb-[8px]',
        false: 'pb-0',
      },
    },
    defaultVariants: { position: 'standalone', hasSubLabel: false },
  }
);

/**
 * The floating label.
 *
 * It rises 4px and shrinks from 14px to 12px once the field is filled or focused. Both halves of
 * that are read off the rebuilt set, and both changed in the rework:
 *
 *   rest    `Label text` fs=14, line box at y=13 — the set gives `Container` 4px of top padding on
 *           every isFilled=No cell, and drops it to 0 on every other one
 *   shrunk  `Label text` fs=12, line box at y=9
 *
 * So the movement is 4px where `TextInput`'s is 10px, and the scale is 12/14 where this component's
 * used to be 11/14. Neither number is transferable from next door, and the reason they differ is
 * structural rather than arbitrary: a single-line field centres its resting label in a 60px box, so
 * the label has the whole box to fall through. Here the resting label sits on its own line ABOVE
 * the value, because the value grows downwards and a centred label would drift away from the text it
 * names — so it only has the 4px of padding to give up.
 *
 * An earlier revision of this port read the label as not moving at all, from a mid-rework state of
 * the file where the 4px padding was absent and all 48 cells drew it at y=9. That reading is dead;
 * the cells are consistent now and they move it.
 *
 * `top-0` is the padding box, i.e. just inside the 1px border, so the 12px and 8px offsets land the
 * line box at the drawn y=13 and y=9. The vertical centre survives the scale: `origin-left` is
 * `left center`, so a 20px line box scaled about its own centre keeps its text centred — which is
 * the same place a real 12px/20px line box would put it. The shrink is exact rather than
 * approximate.
 *
 * Deliberately NOT `pointer-events-none`. This is a real `<label>` wired to the control by `Field`,
 * so a click on it focuses the textarea natively; making it click-through would instead drop clicks
 * on the part of it that overhangs the control.
 */
export const textAreaLabelRecipe = cva(
  [
    'absolute top-0 left-[24px] origin-left cursor-text select-none',
    'font-sans text-body-md text-[var(--ds-textarea-label)]',
    'truncate',
    'translate-y-[12px] scale-100',

    /**
     * Rise and shrink are ONE composited animation.
     *
     * The properties named are `translate` and `scale`, not `transform`: Tailwind v4's `translate-*`
     * and `scale-*` utilities set the standalone CSS properties of those names, so a transition on
     * `transform` would match nothing and the label would jump to its shrunk position before
     * animating its size.
     *
     * The size change is a `scale` rather than a `font-size` for the same reason it is on the same
     * property as the movement: `font-size` relayouts the text on every frame on the main thread,
     * while `translate`/`scale` are composited, so the two could never stay in step. Scaling also
     * fixes the truncation point, which under a `font-size` animation moves mid-flight and makes the
     * ellipsis jitter on a long label.
     *
     * `scale-100` is stated on the base rather than left implicit, because interpolating from
     * `scale: none` is not reliably animatable.
     */
    'transition-[translate,scale,color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

    // 12/14 — the shrunk size as a ratio of the resting one, since the font-size itself never
    // changes. It used to be 11/14, from a set that drew the floated label at the `label-sm` size,
    // so this is a real change rather than a rounding correction.
    'group-data-[filled]/ti:translate-y-[8px] group-data-[filled]/ti:scale-[0.857143]',
    'group-data-[focused]/ti:translate-y-[8px] group-data-[focused]/ti:scale-[0.857143]',

    /**
     * And whenever a placeholder is showing, which is the third way the value line gets occupied.
     *
     * This is a READING rather than something drawn, and it is worth being explicit about which
     * cell it comes from. The set draws a placeholder in exactly one column — `Active`, the focused
     * one — and there it shrinks the label. It draws no cell at all for an UNFOCUSED field holding a
     * placeholder, because its isFilled=No cells contain no value row.
     *
     * Reproducing the shrink for that undrawn case is the only reading that avoids a collision: the
     * resting label's line box is y=13..33 and the value line is y=29..49, so at rest the two
     * overlap by 4px. Shrinking the label lifts it clear. The overlap is smaller than it was on the
     * retired set — where the resting label sat directly ON the value line and the two stacked into
     * something unreadable — but it is still an overlap, and a field with a placeholder is by
     * definition showing it while empty.
     *
     * `:placeholder-shown` is the exact condition and nothing narrower is needed: it matches only
     * when the element HAS a placeholder and is empty, so a field without one never matches and its
     * label still rests at 14px. Reading it off the control through `group-has-` rather than adding
     * a prop keeps the component's state observed from the DOM, like every other state here.
     */
    'group-has-[:placeholder-shown]/ti:translate-y-[8px] group-has-[:placeholder-shown]/ti:scale-[0.857143]',
    'group-data-[disabled]/ti:cursor-not-allowed group-data-[disabled]/ti:text-[var(--ds-textarea-label-disabled)]',
  ],
  {
    variants: {
      /**
       * How much of the row the label may occupy before it truncates.
       *
       * Two gutters of 24px with nothing trailing. With a trailing button the set narrows the text
       * column by 28px — a 24px cell plus the row's 4px gap — so the label has to stop where the
       * value does, or a long one runs under the button.
       *
       * Emitted as a variant rather than an unconditional `calc()` because both values are literal
       * strings: a Tailwind arbitrary value cannot interpolate, so one entry cannot express both.
       */
      hasRightButton: {
        true: 'max-w-[calc(100%_-_76px)]',
        false: 'max-w-[calc(100%_-_48px)]',
      },
    },
    defaultVariants: { hasRightButton: false },
  }
);

/**
 * The `<textarea>`, which is also the field's entire hit area and where the height floor lives.
 *
 * `pt-[28px]` is the set's 8px top inset plus the 20px label line, which puts the first line of the
 * value at y=29 — where the set draws it (`.Input` y=29) and where `TextInput` and `DatePicker` draw
 * theirs, so the three agree when stacked. Everything above that line is still part of the control
 * rather than dead space on the box.
 *
 * ── The floor, and why it is a pair of numbers rather than one ────────────────────────────────
 *
 * The set floors `Container` — the label line PLUS the value block — at `minHeight: x16` = 64px, and
 * lets the box hug that. So the floor this recipe needs is the 8px top padding, plus that 64px, plus
 * whichever bottom inset the control is currently paying:
 *
 *   no helper   8px top + 64px content + 8px bottom  = 80px, giving a box of 82px
 *   helper      8px top + 64px content + 0           = 72px, giving 72 + 4 + 20 + 8 + 2 = 106px
 *
 * The 20px label line is inside the 64px and NOT added again. It is the trap in this arithmetic,
 * because `pt-[28px]` above is the 8px padding plus that same label line — so reading the floor as
 * "28 + 64 + 8" double-counts it and lands at 102. `TextArea.test.tsx` is arranged so that
 * miscount cannot be written a second time.
 *
 * Both totals are exactly what the set draws, and `TextArea.test.tsx` asserts them as totals rather
 * than as the intermediate numbers, so a future change to where the padding lives has to keep the
 * drawn height rather than keep this arithmetic.
 *
 * `rows` stays the mechanism for a TALLER field and the floor only stops it collapsing: the set
 * describes a field that "grows with its content", which a caller-chosen row count expresses well
 * enough that swapping in `field-sizing: content` would cost more than it buys — it is unsupported
 * in Firefox and silently overrides `rows` where it IS supported, so one documented prop would mean
 * two different things depending on the browser. Recorded as a follow-up rather than taken.
 *
 * ── The resets ─────────────────────────────────────────────────────────────────────────────────
 *
 * Preflight is deliberately absent from the token layer, so the browser's own control styling is
 * still in play and every reset is spelled out rather than assumed. `<textarea>` needs two that
 * `<input>` does not: `align-top`, because a replaced inline-block element sits on the text baseline
 * by default and leaves a few pixels of descender space under the box, and an explicit `resize`.
 *
 * ── Why vertical-only resize ───────────────────────────────────────────────────────────────────
 *
 * `resize-y` rather than the browser's `both` default, and rather than `resize-none`.
 *
 * Horizontal resize has to go: the control is `w-full` inside a bordered box, so widening it pushes
 * the text out through the right border — the box does not grow with it, and in a stacked group the
 * result is one field wider than its neighbours with a seam down the middle of the stack.
 *
 * Vertical resize stays. Removing it entirely is the tidier-looking option, it is what most design
 * systems reach for, and it is what `chatInputControlRecipe` chose for the other multi-line field in
 * this system — so this recipe is knowingly the odd one out. It stays because "this box is too small
 * for what I am writing" is the single most common complaint about a multi-line field, and the
 * grabber is the only affordance that answers it without the component owning an autosize behaviour.
 * Reaffirmed on 2026-09-18 with the cost below on the table.
 *
 * Nothing depends on the height: the box is `flex-col` and grows with the control, and the stacked
 * group collapses borders vertically, so a resized field keeps its seams. The floor above still
 * holds, because `min-height` outranks an inline `height`.
 *
 * ── The grabber does not sit on the box's corner when there is helper text, and cannot ─────────
 *
 * The UA draws the grabber at the CONTROL's bottom-right, and with a helper line the control stops
 * one row short of the box — so the mark floats against the right edge rather than sitting in a
 * corner. It looks like a stray glyph, and it is the first thing anyone notices about this field.
 *
 * The obvious fix does not work, and it fails in a way that testing casually will not show. Spanning
 * the control across the whole box and overlaying the helper inside its `padding-bottom` DOES put
 * the grabber exactly on the box's corner, and both drawn heights still come out at 82px and 106px.
 * But a textarea's bottom padding is part of its scrollable area: at MAX scroll the padding holds
 * and the helper is clear, which is what a quick check shows — while mid-scroll the value renders
 * straight through the helper text. Measured 2026-09-18 on a 12-line value at `scrollTop: 60` of
 * 136, two lines overlapping the error string.
 *
 * So the options are a grabber in an odd place, or value text over error text. The first is a
 * cosmetic complaint and the second is a legibility bug, and the grabber is what the design asked to
 * keep. If this is revisited, `resize-none` (matching `ChatInput`) is the move rather than the
 * overlay — the design draws no drag handle on any cell, only the scrollbar.
 *
 * ── The scrollbar ──────────────────────────────────────────────────────────────────────────────
 *
 * The set draws a 4px fully-rounded thumb over no track, and its own description calls it "a
 * depiction of the browser scrollbar". So it is reproduced as the real scrollbar rather than as a
 * layer — a drawn rectangle would not scroll anything.
 *
 * Both syntaxes are needed and neither is redundant: `scrollbar-width`/`scrollbar-color` is the
 * standard property Firefox implements, and the `::-webkit-scrollbar` pseudo-elements are what
 * Chrome and Safari honour. `Dialog.recipe` and `chatInputControlRecipe` take the same pair.
 * `scrollbar-width: thin` is the closest the standard property gets to 4px; the exact width is only
 * expressible through the WebKit pseudo-element, so the two agree in colour and shape and differ in
 * precision. This is the sanctioned case for customising a scrollbar — a small element, not the
 * page.
 *
 * Not dimmed when the field is disabled, and there is no `-scrollbar-disabled` slot to dim it with:
 * a field you cannot edit can still need scrolling to be READ. The set paints the thumb role on its
 * disabled cells too, but the layer is hidden there, so its value is a hidden layer's leftover
 * rather than a specification.
 *
 * One honest deviation, and it is the platform's rather than a choice: the set draws the thumb
 * PERSISTENTLY on every filled cell, and on macOS the browser's scrollbar is an overlay that fades
 * out when the user is not scrolling. So a filled, scrollable field shows no thumb at rest there
 * even though the colour is correctly bound — verified 2026-09-17 by reading
 * `scrollbar-color: rgb(74, 78, 83) transparent` off the rendered control while it was scrollable.
 * The only way to match the drawing would be to paint a fake thumb, which would not scroll anything;
 * the set's own description calls this "a depiction of the browser scrollbar", so the browser's
 * behaviour is the faithful reading.
 */
const controlBase = [
  /**
   * Inset from the box's right edge, and from its bottom below (`hasSubLabel`), so the grabber the
   * browser draws at the control's bottom-right corner lands inside the box's 16px radius instead of
   * poking out through the curve. The paddings give the same amount back, so the text column and the
   * drawn heights do not move, and a press on the inset strip still reaches the control through the
   * box's `forwardBoxPress`.
   */
  'm-0 mr-1.5 block w-[calc(100%_-_--spacing(1.5))] appearance-none border-0 bg-transparent align-top',
  'font-sans text-body-md text-[var(--ds-textarea-value)]',
  PHONE_INPUT_SIZE,
  'outline-none',
  // `resize` is NOT here: it moved to the `autoGrow` variant below, so exactly one rule ever sets
  // it and the two cannot resolve by emission order.
  'px-[24px] pt-[28px]',
  'cursor-text',
  'placeholder:text-[var(--ds-textarea-placeholder)]',
  'disabled:cursor-not-allowed disabled:resize-none disabled:text-[var(--ds-textarea-value-disabled)]',
  /**
   * The disabled PLACEHOLDER needs its own rule, and `disabled:text-*` above is not enough.
   *
   * `::placeholder` takes `color` by inheritance, so the rule above would reach it for free — but
   * the line before it sets `placeholder:text-*` explicitly, and an explicit `::placeholder` colour
   * beats an inherited one. Without this, the one string a disabled EMPTY field shows renders at
   * full strength.
   *
   * That was survivable while the disabled FILL was visibly different from the resting one. It is
   * not, as of the 2026-09-18 light ladder sync: `background/control` and `-disabled` both resolve
   * to `neutral/50` — pure white — in light, so the fill step is gone in that mode and the
   * signals are a border pair 2.15:1 apart and the text. A placeholder left at full strength makes
   * a disabled field read as an active one.
   *
   * `textInputPillControlRecipe` carries the identical rule for the identical reason, and its note
   * has the longer version. The 60px input does not, because it has no placeholder to dim.
   */
  'disabled:placeholder:text-[var(--ds-textarea-value-disabled)]',

  '[scrollbar-width:thin] [scrollbar-color:var(--ds-textarea-scrollbar)_transparent]',
  '[&::-webkit-scrollbar]:w-[4px]',
  '[&::-webkit-scrollbar-track]:bg-transparent',
  '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--ds-textarea-scrollbar)]',
];

export const textAreaControlRecipe = cva(controlBase, {
  variants: {
    /**
     * When a sub-label row follows — helper text, error text, a character counter, or any
     * combination — the bottom inset moves to the box (see `textAreaBoxRecipe`) and the floor drops
     * by the same 8px, so the CONTENT floor stays at the set's 64px either way.
     *
     * Named for the ROW rather than for helper text, which is what it was called until the counter
     * arrived. The distinction is not cosmetic: a field with `maxLength` and no `helperText` renders
     * the row and has to pay this inset, and under the old name that call site read as though it
     * were lying about having helper text.
     */
    hasSubLabel: {
      true: 'min-h-[72px] pb-0',
      // 74 + the 6px bottom inset is the 80 it replaces, and pb-0.5 + that inset is the old 8px.
      false: 'mb-1.5 min-h-[74px] pb-0.5',
    },
    /**
     * 24px gutter, plus a 24px trailing cell and the 4px gap the set puts between it and the text
     * column (the column ends at x=239, the cell starts at x=243). The same 52px `TextInput` pays,
     * because it is the same Button in the same place — less the 6px the control is inset by.
     */
    hasRightButton: { true: 'pr-[46px]', false: 'pr-[18px]' },
    /**
     * On when the caller passed `maxRows`, which is what turns the field into an auto-growing one.
     *
     * The grabber has to go with it, and this is a conflict rather than a preference: auto-growth
     * writes the element's inline `height` on every keystroke, so a height the user dragged to
     * would be overwritten by their next character. Offering a control that undoes itself is worse
     * than not offering it — see the long note above on why vertical resize is otherwise kept.
     *
     * `overflow-y-auto` rather than the browser default, so the scrollbar appears at exactly the
     * moment growth stops at the cap. That is also when the set's `isScrollable` cell is the right
     * depiction of the field, which is the whole reason that boolean is off by default.
     */
    autoGrow: {
      true: 'resize-none overflow-y-auto',
      false: 'resize-y',
    },
  },
  defaultVariants: { hasSubLabel: false, hasRightButton: false, autoGrow: false },
});

/**
 * Where the trailing BUTTON sits. Position only — no size, no colour, no radius.
 *
 * All three come from the `IconButton` this is applied to, which is the point: the set draws the
 * trailing affordance as an instance of `Button` (`Size=ExtraSmall, Type=Muted, iconOnly=Yes`), so
 * reproducing it means rendering that component rather than approximating its resting cell with a
 * styled `<span>`. A span cannot express the hover and pressed cells at all.
 *
 * `top-[8px]` is where this field differs from `TextInput`, and it is authored rather than
 * accidental. That field centres its 24px cell in a 60px box (`top-[17px]`); this one TOP-ALIGNS it,
 * because `Container` is `counterAxisAlignItems: MIN` and the cell lands at y=9 — flush with the
 * label line — on every cell where the button is visible. Centring it in a field that grows with its
 * content would put it in a different place at every row count, which is presumably why the design
 * pins it to the top instead.
 *
 * The 8px is the set's own top inset; `top-0` is the padding box, just inside the 1px border, so
 * 8px lands the cell at the drawn y=9.
 *
 * `z-10` because the control is a full-bleed sibling underneath it — without a stacking order the
 * textarea's own box paints over the button on some engines once the field scrolls.
 *
 * Still under the 44px touch target the product's accessibility rules ask for, in Figma as much as
 * here — the same flag `textInputTrailingButtonRecipe` carries, and the same answer: a 44px control
 * would not fit the drawn layout, so it is a design question rather than something to silently grow.
 */
export const textAreaTrailingButtonRecipe = cva(['absolute top-[8px] right-[24px] z-10']);

/**
 * Helper / error text, rendered inside the border as `TextInput`'s 60px variant does.
 *
 * Inside rather than below, and that placement is what makes the stacked group work:
 * `FieldGroup` collapses the borders of adjacent CHILDREN, so anything rendered outside the box
 * would sit between two borders that are supposed to be one line. A field whose helper text broke
 * the seam could not satisfy the group contract at all.
 */
export const textAreaHelperRecipe = cva([
  // `min-w-0` with `flex-1` is what lets a long helper string WRAP instead of shoving the counter
  // out of the box: a flex item's default `min-width: auto` refuses to shrink below its content.
  'flex min-w-0 flex-1 items-center',
  // `m-0` is load-bearing rather than tidiness: `Field.Description` renders a `<p>`, and with
  // Preflight absent it keeps the browser's default `margin: 1em 0` — 28px of stray space that the
  // `<div>` `Field.Error` renders does not have, so the box would change height with the state.
  'm-0',
  // No gutter of its own — `textAreaSubLabelRecipe` pays the 24px for the whole row, so the helper
  // and the counter share one pair of margins and cannot drift apart. The size comes from `Body/sm`,
  // the scale's own helper-and-caption style, and its 20px line box is what the 106px total above
  // is built from.
  'font-sans text-body-sm text-[var(--ds-textarea-helper)]',
  /**
   * Mutually exclusive rather than ordered, the same discipline `textAreaBoxRecipe` uses and for the
   * same reason: which of two same-property utilities wins is decided by their order in the
   * GENERATED stylesheet, not by their order in this array.
   *
   * A disabled field is never red. The set's Disabled cells wash every string in the box to the one
   * disabled foreground, helper text included, and that holds whether or not the field also carries
   * an error. This rule is new — the retired set was never read for it, so a disabled errored field
   * used to keep full-strength red helper text.
   */
  'group-[[data-invalid]:not([data-disabled])]/ti:text-[var(--ds-textarea-helper-invalid)]',
  'group-data-[disabled]/ti:text-[var(--ds-textarea-label-disabled)]',
]);

/**
 * The sub-label row — helper or error text on the left, the character counter on the right.
 *
 * One row rather than two stacked lines, per the design review: the counter is a secondary reading
 * of the same field state the helper text describes, and giving it its own line would add 24px to
 * every length-capped field to say something nobody reads twice.
 *
 * Figma draws this as `Sub-label` — node 1626:31292, inside the `Text Area` set 1437:128678 —
 * aligned MAX with a FILL helper. `ml-auto` on the counter is the same mechanism and was the reason
 * the set was changed to match: both this and space-between look identical while two children are
 * visible, and space-between collapses to the START the moment only one is — which put a lone
 * counter on the left. A counter with no helper text beside it is a real shape, so that case had to
 * be the one the alignment is chosen for.
 *
 * Re-measured against the set 2026-09-21 by driving the three booleans on an instance: a lone
 * counter is 42px wide and ends on the row's right edge, and helper-plus-counter splits
 * 200 + 8 + 42 across the same 250px row. The cell goes 82px to 106px when the row opens.
 *
 * ── The one place this DEVIATES from the set ───────────────────────────────────────────────────
 *
 * `items-start`, where the Figma frame is `counterAxisAlignItems: CENTER`. The two are identical in
 * every cell the set can draw, because its helper is a FIXED 20px single line — so the set has no
 * opinion here rather than a contrary one. In code the helper WRAPS (`min-w-0 flex-1`, see
 * `textAreaHelperRecipe`), and centring would then float the counter to the vertical middle of a
 * two-line block it has nothing to do with. `items-start` keeps it on the first line, level with
 * the text a reader starts on.
 *
 * The 24px gutter lands both children on the same margin as the value text above them.
 */
export const textAreaSubLabelRecipe = cva(['flex items-start gap-2', 'px-[24px]']);

/**
 * The character counter.
 *
 * No slot of its own: it reads `--ds-textarea-helper`, and at the limit `--ds-textarea-helper-invalid`
 * — the same pair the helper text uses. That is the point rather than a shortcut. A counter is a
 * sub-label, it sits on the sub-label line, and giving it a private token would be inventing a third
 * foreground role for a string that should always match the one beside it.
 *
 * `tabular-nums` is load-bearing. Proportional digits change width as the count climbs, so a
 * right-aligned counter shifts left and right by a pixel or two on almost every keystroke — small
 * enough to look like a rendering fault rather than a feature, and distracting in exactly the field
 * where someone is concentrating on their words.
 *
 * `shrink-0` keeps it whole when the helper text is long; the helper wraps instead — see
 * `textAreaHelperRecipe`.
 *
 * ── The colour at the limit ────────────────────────────────────────────────────────────────────
 *
 * `atLimit` is driven by the COUNT reaching `maxLength`, not by the field's `error` state, and the
 * two are deliberately independent. A full field is not invalid: the browser stops accepting input
 * at `maxLength`, so the user cannot submit too much text and there is nothing to correct. The red
 * is telling them why typing stopped working, which is a different message from "you got this
 * wrong" — and it is the only signal available, since a `<textarea>` silently discards the
 * keystrokes past its cap.
 *
 * It is not the sole carrier of that message either: the number reads `1000/1000`, which says the
 * same thing without colour (WCAG 1.4.1).
 */
export const textAreaCounterRecipe = cva(
  [
    'ml-auto shrink-0',
    'font-sans text-body-sm tabular-nums',
    'text-[var(--ds-textarea-helper)]',
    'group-data-[disabled]/ti:text-[var(--ds-textarea-label-disabled)]',
  ],
  {
    variants: {
      /**
       * Fenced against `data-disabled` rather than ordered after it, the same discipline every other
       * recipe in this file uses: which of two same-property utilities wins is decided by their
       * order in the GENERATED stylesheet, not by their order in this file. A disabled field is
       * never red, including a disabled field that happens to be full.
       */
      atLimit: {
        true: 'group-[:not([data-disabled])]/ti:text-[var(--ds-textarea-helper-invalid)]',
        false: '',
      },
    },
    defaultVariants: { atLimit: false },
  }
);
